import type { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import IconButton from '../Button/IconButton'
import LineAngleLeftIcon from '../Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from '../Icons/line/LineAngleRightIcon'
import LightBox from '../Modal/LightBox'
import BodyText from '../Typography/BodyText'
import type { ImageCarouselRefInterface } from './BaseImageCarousel'
import BaseImageCarousel from './BaseImageCarousel'
import type ImageCarousel from './ImageCarousel'
import ImageGalleryThumbnailStrip from './ImageGalleryThumbnailStrip'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getImageAtCarouselIndex } from './imageGalleryUtils'
import { useLightboxBackgroundClose } from './useLightboxBackgroundClose'
import useCarouselArrowKind from './useCarouselArrowKind'

const NavigationArrow = styled(IconButton)`
  display: none;
  z-index: 1;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const GalleryLightBox = styled.div`
  height: 100%;
  display: grid;
  align-items: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 10% 1fr 10%;

    > ${NavigationArrow} {
      align-self: center;
      justify-self: center;
    }
  }

  > [hidden] {
    opacity: 0;
    pointer-events: none;
  }
`

const GalleryReservedRatio = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  position: relative;
  max-height: 100%;
  width: 100%;
  max-width: ${rem(1200)};
`

const TopBottomTextSection = styled(BodyText)`
  display: flex;
  align-items: start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 ${rem(12)};
`

const GalleryElement = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${rem(12)};
  overflow: hidden;
  min-width: 0;
`

const MediaBlock = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  padding: 0 ${rem(12)};
`

const ThumbnailStripWrapper = styled.div`
  flex-shrink: 0;
  overflow: hidden;
  margin-bottom: ${rem(40)};
`

const galleryImageProps: ImageParams = { mobileAspectRatio: '16:9', quality: 'good', fit: 'center' }

type ImageCarouselProps = Pick<React.ComponentProps<typeof ImageCarousel>, 'images' | 'interstitials' | 'onImageChange' | 'defaultIndex'>

interface Props extends React.PropsWithChildren, ImageCarouselProps {
  /** Whether gallery arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean
}

function ImageGalleryLightboxWithThumbnails(props: Props) {
  const {
    images,
    children,
    defaultIndex = 0,
    onImageChange,
    interstitials = EmptyArray,
    arrowIconRtlFlip = false,
  }: Props = props
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const [currentImage, setCurrentImage] = useState<App.Image | undefined>(() => getImageAtCarouselIndex(defaultIndex, images, interstitials))
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)
  const onBackgroundMouseDown = useLightboxBackgroundClose()
  const intl = useIntl()
  const arrowKind = useCarouselArrowKind()

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        carouselRef.current?.previousImage()
      } else if (e.key === 'ArrowRight') {
        carouselRef.current?.nextImage()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const onArrowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const direction = e.currentTarget.dataset.direction as string
    if (direction === 'left') {
      carouselRef.current?.previousImage()
    } else {
      carouselRef.current?.nextImage()
    }
  }, [])

  const onChangeImage = useCallback((index: number, image?: App.Image) => {
    onImageChange?.(index, image)
    setCurrentImage(image)
    setCurrentImageIndex(index)
  }, [onImageChange])

  const onThumbnailSelect = useCallback((index: number) => {
    carouselRef.current?.goToIndex(index, 'instant')
  }, [])

  const videoMeta = useMemo(() => {
    const firstVideo = interstitials.find(i => i.id.startsWith('video-'))
    const videoId = firstVideo?.id.replace('video-', '')
    const videoPosition = typeof firstVideo?.position === 'number' ? firstVideo.position : undefined
    return {
      videoId,
      videoPosition,
    }
  }, [interstitials])

  return (
    <LightBox>
      <GalleryLightBox onMouseDown={onBackgroundMouseDown}>
        <NavigationArrow
          onClick={onArrowClick}
          kind={arrowKind}
          variant="dark"
          shape="circle"
          size="medium"
          data-direction="left"
          hidden={images.length + interstitials.length <= 1}
        >
          <LineAngleLeftIcon rtlFlip={arrowIconRtlFlip} />
        </NavigationArrow>
        <GalleryElement onMouseDown={onBackgroundMouseDown}>
          <TopBottomTextSection variant="medium" colour="dark-contrast" align="center">
            {intl.formatMessage(
              {
                id: '656j3YKzQJ8ADaozS-zC',
                defaultMessage: '{current} / {total}',
                description: 'Image counter showing current position out of total media items in the fullscreen gallery lightbox',
              },
              {
                current: currentImageIndex + 1,
                total: images.length + interstitials.length,
              },
            )}
          </TopBottomTextSection>
          <MediaBlock onMouseDown={onBackgroundMouseDown}>
            <GalleryReservedRatio>
              <BaseImageCarousel
                defaultIndex={defaultIndex}
                imageParams={galleryImageProps}
                carouselRef={carouselRef}
                images={images}
                onImageChange={onChangeImage}
                interstitials={interstitials}
              />
              {children}
            </GalleryReservedRatio>
            <TopBottomTextSection variant="medium" colour="dark-contrast" align="center">
              {currentImage?.title}&nbsp;
            </TopBottomTextSection>
          </MediaBlock>
          <ThumbnailStripWrapper>
            <ImageGalleryThumbnailStrip
              images={images}
              videoIds={videoMeta.videoId ? [videoMeta.videoId] : EmptyArray}
              videoPosition={videoMeta.videoPosition}
              selectedIndex={currentImageIndex}
              onSelect={onThumbnailSelect}
              showScrollArrows
              arrowIconRtlFlip={arrowIconRtlFlip}
            />
          </ThumbnailStripWrapper>
        </GalleryElement>
        <NavigationArrow
          onClick={onArrowClick}
          className="right"
          variant="dark"
          kind={arrowKind}
          shape="circle"
          size="medium"
          data-direction="right"
          hidden={images.length + interstitials.length <= 1}
        >
          <LineAngleRightIcon rtlFlip={arrowIconRtlFlip} />
        </NavigationArrow>
      </GalleryLightBox>
    </LightBox>
  )
}

export default ImageGalleryLightboxWithThumbnails
